import { BackendActionTypeIds } from '../generated/action-type-ids';
import { ConfirmationDropdown } from '../components/action-value-inputs/native-dropdowns/confirmation-dropdown';
import { CustomsContentDropdown } from '../components/action-value-inputs/native-dropdowns/customs-content-dropdown';
import { NonDeliveryDropdown } from '../components/action-value-inputs/native-dropdowns/non-delivery-dropdown';
import { DeliveryTemplateDropdown } from '../components/action-value-inputs/native-dropdowns/delivery-template-dropdown';
import { FulfillmentServiceDropdown } from '../components/action-value-inputs/native-dropdowns/fulfillment-service-dropdown';
import { TextParameterInput, TextAreaParameterInput } from '../components/action-value-inputs/generic-inputs';
import { TagDropdown } from '../components/action-value-inputs/native-dropdowns/tag-dropdown';
import { PackingSlipDropdown } from '../components/action-value-inputs/native-dropdowns/packing-slip-dropdown';
import { ShipmentTemplateDropdown } from '../components/action-value-inputs/native-dropdowns/shipment-template-dropdown';
import { UserDropdown } from '../components/action-value-inputs/native-dropdowns/user-dropdown';
import { WarehouseDropdown } from '../components/action-value-inputs/native-dropdowns/warehouse-dropdown';
import {
  AdaptedHoldOrderFor,
  AdaptedSendAnEmail,
  AdaptedCreateAlert,
  AdaptedSellerDefaultWeightInput,
  AdaptedSetPackageDimensions,
  AdaptedHoldUntil,
  AdaptedSetCarrierServicePackage,
  AdaptedChargeShippingToThirdParty,
  AdaptedSetFulfillmentProvider,
  AdaptedSetOrderAsDangerousGoods,
  AdaptedSetDaysAroundDate,
  AdaptedShippingAccountSearchableDropdown,
  AdaptedCollectPaymentOnDelivery,
  AdaptedAddTaxIdentifiers,
  AdaptedSetServiceCollection,
  AdaptedSetPrepayDutiesAndTaxes,
  AdaptedCarrierNotifications,
  AdaptedSetDateFromDaysAfterOrderDate,
  AdaptedSetContainsDangerousGoods,
  AdaptedAddShipmentItem,
  AdaptedAddIOR,
  AdaptedAddServiceLevelRoyalMail,
  AdaptedSetShipDate,
} from '../components/action-value-inputs/ar-adapted-inputs';
import { InsuranceDropdown } from '../components/action-value-inputs/native-dropdowns/insurance-dropdown';
import { ShipDateAuxiliaryInput } from '../components/action-value-inputs/ship-date-auxiliary-input/ship-date-auxiliary-input';
import { DateTypes } from '../../automation-rules/modals/rule-editor-drawer/rule-actions/components/action-value-input/set-ship-date/set-ship-date';
import { actionMessages } from './messages';
import type { ActionRegistry } from './registry-types';
import { WORKFLOW_ACTION_TYPE } from '../../api/automation-workflow-types';

// NOTE: BackendActionTypeIds.PackageType (3) is intentionally excluded.
// It was never seeded into dbo.OrderRuleActionType and is not returned by GetAllAutomationRuleTypes().
// Package selection is handled through BackendActionTypeIds.ShippingService (2) instead.

export const OrderActionRegistry: ActionRegistry = {
  [BackendActionTypeIds.AddImporterOfRecord]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.AddImporterOfRecord,
    payloadProperty: 'intParameter',
    valueInput: AdaptedAddIOR,
  },
  [BackendActionTypeIds.AddServiceLevel]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.AddServiceLevel,
    payloadProperty: 'parameter',
    valueInput: AdaptedAddServiceLevelRoyalMail,
  },
  [BackendActionTypeIds.AddShipmentItem]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.AddShipmentItem,
    payloadProperty: 'compound',
    valueInput: AdaptedAddShipmentItem,
  },
  [BackendActionTypeIds.AddTag]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.AddTag,
    labelMessage: actionMessages.addTag,
    payloadProperty: 'intParameter',
    valueInput: TagDropdown,
  },
  [BackendActionTypeIds.AdjustWeight]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.AdjustWeight,
    labelMessage: actionMessages.adjustWeight,
    payloadProperty: 'compound',
    valueInput: AdaptedSellerDefaultWeightInput,
  },
  [BackendActionTypeIds.Alcohol]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.Alcohol,
    labelMessage: actionMessages.markAlcohol,
    payloadProperty: 'none',
  },
  [BackendActionTypeIds.Alert]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.Alert,
    labelMessage: actionMessages.createAlert,
    payloadProperty: 'compound',
    valueInput: AdaptedCreateAlert,
  },
  [BackendActionTypeIds.AssignToUser]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.AssignToUser,
    labelMessage: actionMessages.assignToUser,
    payloadProperty: 'parameter',
    valueInput: UserDropdown,
  },
  [BackendActionTypeIds.BillDutiesToSender]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.BillDutiesToSender,
    labelMessage: actionMessages.billDutiesToSender,
    payloadProperty: 'none',
  },
  [BackendActionTypeIds.BillTo3rdParty]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.BillTo3rdParty,
    payloadProperty: 'parameter',
    valueInput: AdaptedChargeShippingToThirdParty,
  },
  // BillToOtherAccount in the backend enum maps to "charge to my account" in the UI
  [BackendActionTypeIds.BillToOtherAccount]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.BillToOtherAccount,
    labelMessage: actionMessages.chargeToMyAccount,
    payloadProperty: 'intParameter',
    valueInput: AdaptedShippingAccountSearchableDropdown,
  },
  [BackendActionTypeIds.BlockAmazonLogistics]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.BlockAmazonLogistics,
    labelMessage: actionMessages.blockAmazonLogistics,
    payloadProperty: 'none',
  },
  // COD in the backend enum maps to "collect payment on delivery" in the UI
  [BackendActionTypeIds.COD]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.COD,
    payloadProperty: 'parameter',
    valueInput: AdaptedCollectPaymentOnDelivery,
  },
  [BackendActionTypeIds.Confirmation]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.Confirmation,
    labelMessage: actionMessages.requestConfirmation,
    payloadProperty: 'intParameter',
    valueInput: ConfirmationDropdown,
  },
  [BackendActionTypeIds.CustomsContent]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.CustomsContent,
    labelMessage: actionMessages.setCustomsContentType,
    payloadProperty: 'parameter',
    valueInput: CustomsContentDropdown,
  },
  [BackendActionTypeIds.DangerousGoods]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.DangerousGoods,
    labelMessage: actionMessages.dangerousGoods,
    payloadProperty: 'compound',
    valueInput: AdaptedSetOrderAsDangerousGoods,
  },
  [BackendActionTypeIds.DeclarationStatement]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.DeclarationStatement,
    labelMessage: actionMessages.declarationStatement,
    payloadProperty: 'parameter',
    valueInput: TextParameterInput,
  },
  [BackendActionTypeIds.DelegateFulfillmentProviderService]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.DelegateFulfillmentProviderService,
    labelMessage: actionMessages.delegateFulfillmentService,
    payloadProperty: 'intParameter',
    valueInput: FulfillmentServiceDropdown,
  },
  [BackendActionTypeIds.DeliverByDate]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.DeliverByDate,
    labelMessage: actionMessages.setDeliverByDate,
    payloadProperty: 'intParameter',
    valueInput: AdaptedSetDateFromDaysAfterOrderDate,
  },
  [BackendActionTypeIds.DeliveryEmailTemplate]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.DeliveryEmailTemplate,
    labelMessage: actionMessages.useDeliveryNotificationTemplate,
    payloadProperty: 'intParameter',
    valueInput: DeliveryTemplateDropdown,
  },
  [BackendActionTypeIds.Dimensions]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.Dimensions,
    labelMessage: actionMessages.setPackageDimensions,
    payloadProperty: 'compound',
    valueInput: AdaptedSetPackageDimensions,
  },
  [BackendActionTypeIds.DoNotNotifyMarketplaceOfShipment]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.DoNotNotifyMarketplaceOfShipment,
    labelMessage: actionMessages.doNotNotifyMarketplace,
    payloadProperty: 'none',
  },
  [BackendActionTypeIds.DryIce]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.DryIce,
    labelMessage: actionMessages.markDryIce,
    payloadProperty: 'none',
  },
  [BackendActionTypeIds.DryIceWeight]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.DryIceWeight,
    labelMessage: actionMessages.setDryIceWeight,
    payloadProperty: 'compound',
    valueInput: AdaptedSellerDefaultWeightInput,
  },
  [BackendActionTypeIds.Email]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.Email,
    labelMessage: actionMessages.sendEmail,
    payloadProperty: 'compound',
    valueInput: AdaptedSendAnEmail,
  },
  [BackendActionTypeIds.EmailTemplate]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.EmailTemplate,
    labelMessage: actionMessages.useShipmentNotificationTemplate,
    payloadProperty: 'intParameter',
    valueInput: ShipmentTemplateDropdown,
  },
  [BackendActionTypeIds.FillService]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.FillService,
    payloadProperty: 'parameter',
    valueInput: AdaptedSetFulfillmentProvider,
  },
  [BackendActionTypeIds.Hold]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.Hold,
    labelMessage: actionMessages.holdOrder,
    payloadProperty: 'intParameter',
    valueInput: AdaptedHoldOrderFor,
  },
  [BackendActionTypeIds.HoldUntil]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.HoldUntil,
    labelMessage: actionMessages.holdUntil,
    payloadProperty: 'parameter',
    valueInput: AdaptedHoldUntil,
  },
  [BackendActionTypeIds.Ignore]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.Ignore,
    labelMessage: actionMessages.doNotImport,
    payloadProperty: 'none',
  },
  [BackendActionTypeIds.IncludeReturnLabel]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.IncludeReturnLabel,
    payloadProperty: 'compound',
    valueInput: AdaptedSetCarrierServicePackage,
  },
  [BackendActionTypeIds.Insure]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.Insure,
    labelMessage: actionMessages.insurePackage,
    payloadProperty: 'intParameter',
    valueInput: InsuranceDropdown,
  },
  [BackendActionTypeIds.InternalNote]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.InternalNote,
    labelMessage: actionMessages.addInternalNote,
    payloadProperty: 'parameter',
    valueInput: TextAreaParameterInput,
  },
  [BackendActionTypeIds.NonDelivery]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.NonDelivery,
    labelMessage: actionMessages.setNonDelivery,
    payloadProperty: 'parameter',
    valueInput: NonDeliveryDropdown,
  },
  [BackendActionTypeIds.NonMachinable]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.NonMachinable,
    labelMessage: actionMessages.markNonMachinable,
    payloadProperty: 'none',
  },
  [BackendActionTypeIds.NoPostage]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.NoPostage,
    labelMessage: actionMessages.noPostage,
    payloadProperty: 'none',
  },
  [BackendActionTypeIds.NoteToBuyer]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.NoteToBuyer,
    labelMessage: actionMessages.noteToBuyer,
    payloadProperty: 'parameter',
    valueInput: TextAreaParameterInput,
  },
  [BackendActionTypeIds.OrderContainsDangerousGoods]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.OrderContainsDangerousGoods,
    payloadProperty: 'compound',
    valueInput: AdaptedSetContainsDangerousGoods,
  },
  [BackendActionTypeIds.PackingSlip]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.PackingSlip,
    labelMessage: actionMessages.usePackingSlip,
    payloadProperty: 'intParameter',
    valueInput: PackingSlipDropdown,
  },
  [BackendActionTypeIds.PrepaidDuties]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.PrepaidDuties,
    payloadProperty: 'parameter',
    valueInput: AdaptedSetPrepayDutiesAndTaxes,
  },
  [BackendActionTypeIds.SaturdayDelivery]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.SaturdayDelivery,
    labelMessage: actionMessages.saturdayDelivery,
    payloadProperty: 'none',
  },
  [BackendActionTypeIds.SendCarrierNotifications]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.SendCarrierNotifications,
    payloadProperty: 'parameter',
    valueInput: AdaptedCarrierNotifications,
  },
  [BackendActionTypeIds.ServiceCollection]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.ServiceCollection,
    payloadProperty: 'parameter',
    valueInput: AdaptedSetServiceCollection,
  },
  [BackendActionTypeIds.SetCustomField1]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.SetCustomField1,
    labelMessage: actionMessages.setCustomField1,
    payloadProperty: 'parameter',
    valueInput: TextParameterInput,
  },
  [BackendActionTypeIds.SetCustomField2]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.SetCustomField2,
    labelMessage: actionMessages.setCustomField2,
    payloadProperty: 'parameter',
    valueInput: TextParameterInput,
  },
  [BackendActionTypeIds.SetCustomField3]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.SetCustomField3,
    labelMessage: actionMessages.setCustomField3,
    payloadProperty: 'parameter',
    valueInput: TextParameterInput,
  },
  [BackendActionTypeIds.ShipByDate]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.ShipByDate,
    labelMessage: actionMessages.setShipByDate,
    payloadProperty: 'compound',
    valueInput: AdaptedSetDaysAroundDate,
  },
  [BackendActionTypeIds.ShipDate]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.ShipDate,
    labelMessage: actionMessages.setShipDate,
    payloadProperty: 'compound',
    valueInput: AdaptedSetShipDate,
    auxiliaryInput: {
      component: ShipDateAuxiliaryInput,
      isVisible: (draft) => draft.parameter?.includes(DateTypes.CurrentDate) ?? false,
    },
  },
  [BackendActionTypeIds.ShipperRelease]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.ShipperRelease,
    labelMessage: actionMessages.shipperRelease,
    payloadProperty: 'none',
  },
  [BackendActionTypeIds.ShippingService]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.ShippingService,
    payloadProperty: 'compound',
    valueInput: AdaptedSetCarrierServicePackage,
  },
  [BackendActionTypeIds.ShowPostage]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.ShowPostage,
    labelMessage: actionMessages.showPostagePaid,
    payloadProperty: 'none',
  },
  [BackendActionTypeIds.Stop]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.Stop,
    labelMessage: actionMessages.stopProcessing,
    payloadProperty: 'none',
  },
  [BackendActionTypeIds.TaxIdentifiers]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.TaxIdentifiers,
    labelMessage: actionMessages.addTaxIdentifiers,
    payloadProperty: 'parameter',
    valueInput: AdaptedAddTaxIdentifiers,
  },
  [BackendActionTypeIds.UseBlankBox]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.UseBlankBox,
    labelMessage: actionMessages.useBlankBox,
    payloadProperty: 'none',
  },
  [BackendActionTypeIds.Warehouse]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.Warehouse,
    labelMessage: actionMessages.setShipFrom,
    payloadProperty: 'intParameter',
    valueInput: WarehouseDropdown,
  },
  [BackendActionTypeIds.Weight]: {
    actionType: WORKFLOW_ACTION_TYPE.orderRuleAction,
    actionTypeId: BackendActionTypeIds.Weight,
    labelMessage: actionMessages.setTotalWeight,
    payloadProperty: 'compound',
    valueInput: AdaptedSellerDefaultWeightInput,
  },
};
